import React, { useState } from "react";
import { FormHelperText, Typography, useTheme } from "@mui/material";
import DatePicker from "@components/DatePicker/DatePicker";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import Input from "@components/Input/Input";
import { getSideDrawerStyles } from "@components/SideDrawer/SideDrawer.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import type { MovementError } from "@features/vessel-call/vessel-call-status/MovementServices/form/utils.ts";
import type { Movement } from "@services/movement-services/types";
import { useQueryClient } from "@tanstack/react-query";
import { toMinute } from "@utils/date-time";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import z from "zod";
import { useCompleteMovement } from "../hooks/useCompleteMovement";

const asDateOrNull = (v: unknown) => (v == null || v === "" ? null : (v as Date));

const isNotPastDateTime = (value: Date) => {
  const valueMinute = toMinute(value);
  const nowMinute = toMinute(new Date());

  return !!valueMinute && !!nowMinute && valueMinute.getTime() >= nowMinute.getTime();
};

// This form does not have a store defined, so all fields will be managed via local state or props
// => use regular input components and manage their state locally or via props
interface NonDisputeMovementServiceProps {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  currentMovements: Movement[];
}
interface OutletContextType {
  vesselCallDetails: { id: string; vesselId: string };
}
const serviceVesselCall = "bff-vessel-call";
const NonDisputeMovementService: React.FC<NonDisputeMovementServiceProps> = ({
  isOpen,
  onClose,
  currentMovements,
}) => {
  const { vesselCallDetails } = useOutletContext<OutletContextType>();
  const vesselId = vesselCallDetails?.vesselId;
  const vesselCallId = vesselCallDetails?.id;

  const currentMovement = currentMovements?.[0];

  const isBerthToSea = currentMovement?.movementType === "BERTH_TO_SEA";
  const isBerthToBerth = currentMovement?.movementType === "BERTH_TO_BERTH";

  const { t } = useTranslation();
  const [rtd, setRtd] = useState<Date | null>(null);
  const theme = useTheme();
  const [additionalErrors, setAdditionalErrors] = useState<{
    rtd?: string;
    comments?: string;
  }>({});
  const { notify } = useNotification();
  const TEXTAREA_MAX_CHARS = 500;
  const isPrintableKey = (e: React.KeyboardEvent) =>
    e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
  const charCounterSx = {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right",
    color: "text.secondary",
  };
  const sxProps = {
    "& .MuiInputBase-root": {
      borderRadius: "8px",
      borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
      padding: "12px",
      fontSize: theme.typography.body1.fontSize,
      color: theme.palette.text.primary,
    },
    "& textarea": {
      padding: 0,
    },
  };
  const inputSlotProps = {
    root: {
      className: `rounded-lg text-sm px-4 py-2 border`,
      style: {
        borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
        color: theme.palette.text.primary,
      },
    },
    htmlInput: {
      maxLength: TEXTAREA_MAX_CHARS,
    },
  };
  const styles = getSideDrawerStyles(theme);
  const [comments, setComments] = useState("");
  const queryClient = useQueryClient();

  const rtdLabel =
    isBerthToSea || isBerthToBerth
      ? t("vessel-call.movement-services.preview.fields.rtd-readiness")
      : t("vessel-call.movement-services.preview.fields.rtd");

  const rtdSchema = z.object({
    rtd: z
      .date({
        required_error: t("form-validation.required", { label: rtdLabel }),
        invalid_type_error: t("form-validation.required", { label: rtdLabel }),
      })
      .refine(isNotPastDateTime, {
        message: t("form-validation.must-not-be-in-past", { label: rtdLabel }),
      }),
  });
  const commentsOnlySchema = z.object({
    comments: z
      .string()
      .max(
        TEXTAREA_MAX_CHARS,
        t("form-validation.max-length", {
          label: t("vessel-call.movement-services.comments"),
          max: TEXTAREA_MAX_CHARS,
        }),
      )
      .optional(),
  });

  const resetFormValues = () => {
    setRtd(null);
    setComments("");
    setAdditionalErrors({});
  };

  const handleSuccess = async () => {
    onClose("nonDisputeDrawer");
    resetFormValues();
    notify({
      title: t("vessel-call.movement-services.movement-non-dispute-approved"),
      type: "success",
    });
    setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
      });
    }, 2000);
  };

  const handleError = (error: MovementError) => {
    queryClient.invalidateQueries({
      queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
    });
    onClose("approveDrawer");
    resetFormValues();
    notify({
      title: t("common.error"),
      description:
        error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
      type: "error",
    });
  };
  const { completeMovement } = useCompleteMovement({
    handleSuccess,
    handleError,
  });

  const handleSubmit = async () => {
    const payload: {
      status: "NON_DISPUTED";
      rtd?: string;
      comments?: string;
    } = {
      status: "NON_DISPUTED",
    };
    const rtdResult = rtdSchema.safeParse({ rtd });
    const commentsResult = commentsOnlySchema.safeParse({ comments });

    setAdditionalErrors((prev) => ({
      ...prev,
      rtd: rtdResult.success ? undefined : rtdResult.error.flatten().fieldErrors.rtd?.[0],
      comments: commentsResult.success
        ? undefined
        : commentsResult.error.flatten().fieldErrors.comments?.[0],
    }));

    if (!rtdResult.success || !commentsResult.success) {
      return;
    }
    payload.rtd = (rtd as Date).toISOString();
    if (comments.trim().length > 0) {
      payload.comments = comments.trim();
    }

    await completeMovement({
      id: currentMovement.id || "",
      service: serviceVesselCall,
      payload,
    });
  };

  return (
    <div data-testid="nonDispute-movement-service-form" className="max-h-screen overflow-y-auto">
      <SideDrawer
        isOpen={isOpen}
        title={t(`vessel-call.movement-services.approve-title`)}
        onClose={() => {
          onClose("nonDisputeDrawer");
          resetFormValues();
        }}
        submitLabel={t(`vessel-call.movement-services.approve-button`)}
        onSubmit={handleSubmit}
      >
        <div className="px-6">
          <Typography
            variant="body1"
            data-testid="movement-service-form-title"
            sx={{ color: "text.primary", pb: 4 }}
          >
            {t(`vessel-call.movement-services.approve-subtitle`)}
          </Typography>
          <div className="py-3 flex flex-col gap-2">
            <CustomFormLabel>{rtdLabel}</CustomFormLabel>
            <DatePicker
              value={asDateOrNull(rtd)}
              onChange={(d) => {
                setRtd(d);
                const result = rtdSchema.safeParse({ rtd: d as Date });
                setAdditionalErrors((p) => ({
                  ...p,
                  rtd: result.success ? undefined : result.error.flatten().fieldErrors.rtd?.[0],
                }));
              }}
              testId={`rtd-date-input`}
              timePicker={true}
              dateConstraint="not-past"
              combinedInput={true}
              hasError={!!additionalErrors.rtd}
            />
            {additionalErrors.rtd && (
              <p className="text-sm text-red-500 mt-1" data-testid="rtd-error">
                {additionalErrors.rtd}
              </p>
            )}
          </div>
          <div className="col-span-full mb-0 flex flex-col gap-2 mt-6">
            <CustomFormLabel>{t("vessel-call.movement-services.comments")}</CustomFormLabel>
            <Input
              placeholder={t("vessel-call.movement-services.comments")}
              multiline
              minRows={3}
              fullWidth
              variant="outlined"
              value={comments}
              onKeyDown={(e) => {
                if (!isPrintableKey(e)) return;
                if (comments.length >= TEXTAREA_MAX_CHARS) {
                  setAdditionalErrors((prev) => ({
                    ...prev,
                    comments: t("form-validation.max-length", {
                      label: t("vessel-call.movement-services.comments"),
                      max: TEXTAREA_MAX_CHARS,
                    }),
                  }));
                }
              }}
              onBlur={() => {
                setAdditionalErrors((prev) => ({
                  ...prev,
                  comments: undefined,
                }));
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                const value = e.target.value;
                setComments(value);
                const result = commentsOnlySchema.safeParse({
                  comments: value,
                });
                setAdditionalErrors((prev) => ({
                  ...prev,
                  comments: result.success
                    ? undefined
                    : result.error.flatten().fieldErrors.comments?.[0],
                }));
              }}
              slotProps={inputSlotProps}
              sx={sxProps}
              testId={"additional-notes-input"}
            />
            {additionalErrors.comments && (
              <p style={styles.errorText} className="text-sm mt-1">
                {additionalErrors.comments}
              </p>
            )}
            <FormHelperText sx={charCounterSx}>
              {comments.length}/{TEXTAREA_MAX_CHARS}
            </FormHelperText>
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default NonDisputeMovementService;
